import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/auth';
import './auth.css';
import Message from '../../components/ui/Message';
import { InstanceLogo } from '../../components/ui/InstanceLogo';
import { BRAND_NAME, PAYFAC_SIGNUP_ENABLED, REGISTER_ENABLED, SELF_ONBOARDING_ENABLED } from '../../utils/appConfig';
import { User } from '../../models/interfaces';
import { SuspendedAccount } from './SuspendedAccount';

const Login = () => {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [confirmarReset, setConfirmarReset] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [message, setMessage] = useState('');

  const navigate = useNavigate();
  const { status, login, errorMessage, successMessage, removeError, resetPassword, suspendedAccount, clearSuspendedAccount } =
    useContext(AuthContext);

  useEffect(() => {
    const storedEmail = localStorage.getItem('usuarioLogin');
    if (storedEmail) {
      setUser(storedEmail);
      setRememberMe(true);
    }
  }, []);

  const canSubmit = user.trim().length > 0 && password.length > 0;

  const removeMessage = () => setMessage('');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    rememberMe
      ? localStorage.setItem('usuarioLogin', user)
      : localStorage.removeItem('usuarioLogin');

    login({
      correo: user,
      password,
      retry: true
    }).then((loggedUser: User) => {
      if (loggedUser?.tempPassword) {
        navigate('/change-password', { replace: true });
      }
    });
  };

  const toRegister = () => {
    navigate('/signup-payfac', { replace: true });
  };

  const handleConfirmResetPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user.trim()) {
      setMessage('Por favor, ingrese su usuario para restablecer la contraseña.');
      return;
    }
    setConfirmarReset(true);
  };

  const handleResetPassword = () => {
    setConfirmarReset(false);
    resetPassword(user);
  };

  if (status === 'checking') {
    return (
      <div className="auth-page">
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 text-muted">Iniciando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <main className="auth-main">
        {/* COMERCIO SUSPENDIDO POR FALTA DE PAGO: tarjeta del cobro (últimos 4) y opción de cambiarla */}
        {suspendedAccount ? (
          <div className="auth-card">
            <InstanceLogo className="auth-logo d-block mx-auto mb-3" />
            <SuspendedAccount account={suspendedAccount} onClose={() => { clearSuspendedAccount(); setPassword(''); }} />
          </div>
        ) : (
        <div className="auth-card">
          <div className="text-center mb-4">
            <InstanceLogo className="auth-logo" />
            {/* <h4 className="mb-1">Bienvenido a {BRAND_NAME}</h4> */}
            <p className="text-muted mb-0">Ingrese sus datos para continuar.</p>
          </div>

          <form onSubmit={handleLogin} noValidate>
            {/* USUARIO */}
            <div className="mb-3">
              <label htmlFor="email" className="form-label small text-muted mb-1">Usuario</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-person" /></span>
                <input
                  type="text"
                  className="form-control"
                  id="email"
                  placeholder="Su usuario"
                  autoComplete="username"
                  autoFocus={!user}
                  value={user}
                  onChange={e => setUser(e.target.value)}
                />
              </div>
            </div>

            {/* CONTRASEÑA */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label small text-muted mb-1">Contraseña</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-lock" /></span>
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  className="form-control"
                  id="password"
                  placeholder="Su contraseña"
                  autoComplete="current-password"
                  autoFocus={!!user}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="btn auth-toggle border"
                  onClick={() => setPasswordVisible(prev => !prev)}
                  title={passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-label={passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={passwordVisible ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                </button>
              </div>
            </div>

            {/* OPCIONES */}
            <div className="mb-4 d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div className="form-check mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label className="form-check-label small" htmlFor="rememberMe">
                  Recordar usuario
                </label>
              </div>

              <a href="/" onClick={handleConfirmResetPassword} className="small text-decoration-none">
                ¿Olvidó su contraseña?
              </a>
            </div>

            {/* INICIAR SESIÓN */}
            <button type="submit" className="btn btn-primary w-100" disabled={!canSubmit}>
              <i className="bi bi-box-arrow-in-right me-1" /> Iniciar sesión
            </button>
          </form>

          {/* REGISTRO AUTOGESTIONADO (se oculta con REACT_APP_SELF_ONBOARDING_ENABLED=false) */}
          {SELF_ONBOARDING_ENABLED && (
            <button type="button" className="btn btn-outline-primary w-100 mt-3" onClick={() => navigate('/signup')}>
              <i className="bi bi-shop me-1" /> Cree su comercio en minutos
            </button>
          )}

          {/* REGISTRO PAYFAC: solo en la instancia de Swiftpay; se oculta con REACT_APP_REGISTER_ENABLED=false */}
          {REGISTER_ENABLED && PAYFAC_SIGNUP_ENABLED && (
            <div className="text-center mt-4 pt-3 border-top">
              <span className="small text-muted">¿No tiene cuenta? </span>
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline fw-semibold text-decoration-none" onClick={toRegister}>
                Regístrese ahora
              </button>
            </div>
          )}
        </div>
        )}
      </main>

      <footer className="auth-footer">© {new Date().getFullYear()} {BRAND_NAME}</footer>

      {/* MENSAJES */}
      {errorMessage && (
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}

      {message && (
        <Message
          type="error"
          message={message}
          closeDialog={removeMessage}
          handleOk={removeMessage}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}

      {successMessage && (
        <Message
          type="success"
          message={successMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}

      {confirmarReset && (
        <Message
          type="success"
          title="Restablecer contraseña"
          message={`Se enviará una contraseña temporal al correo asociado al usuario "${user.trim()}". ¿Desea continuar?`}
          closeDialog={() => setConfirmarReset(false)}
          showCancel={true}
          handleCancel={() => setConfirmarReset(false)}
          handleOk={handleResetPassword}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}
    </div>
  );
};

export default Login;
